import { DeleteOutlined } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getS3Url } from 'src/constants';
import VehicleSelectField from './VehicleSelectField';

const DriverItem = ({ driver }) => {
  const { t } = useTranslation('translation');
  return (
    <Box
      sx={{
        opacity: driver?.is_available ? 1 : 0.5,
        overflow: 'hidden',
      }}
    >
      <Typography
        noWrap
        textOverflow="ellipsis"
        overflow="hidden"
        fontSize="15px"
        fontWeight={600}
      >
        {driver?.name || t('common.na')} &nbsp;
        {driver?.name &&
          !driver?.is_available &&
          `(${t('common.unavailable')})`}
      </Typography>
      <Typography
        fontSize="12px"
        textTransform="uppercase"
        color="text.secondary"
        variant="body2"
        noWrap
      >
        {driver?.name
          ? `${driver.user_formatted_address || driver.email}`
          : t('common.na')}
      </Typography>
    </Box>
  );
};

const DriverSelector = ({
  drivers = [],
  vehicles = [],
  error,
  driversValue = [],
  onChange,
  sx = {},
}) => {
  const { t } = useTranslation('translation');

  const handleSelectDriver = (driverId) => {
    if (!driverId) {
      onChange([]);
      return;
    }
    // If already added, ignore
    if (driversValue.some((d) => d.user_id === driverId)) return;
    if (driversValue.length >= 3) return;
    onChange([...driversValue, { user_id: driverId, vehicle_id: null }]);
  };

  const handleUpdateVehicle = (driverId, vehicleId) => {
    onChange(
      driversValue.map((d) =>
        d.user_id === driverId ? { ...d, vehicle_id: vehicleId } : d
      )
    );
  };

  const handleRemoveDriver = (driverId) => {
    onChange(driversValue.filter((d) => d.user_id !== driverId));
  };

  return (
    <Box sx={sx}>
      {/* Driver dropdown — adds immediately on select */}
      <FormControl fullWidth>
        <InputLabel shrink>{t('donations.driver')}</InputLabel>
        <Select
          label={t('donations.driver')}
          value=""
          onChange={(e) => handleSelectDriver(e.target.value)}
          displayEmpty
          disabled={driversValue.length >= 3}
          renderValue={() =>
            driversValue.length >= 3
              ? t('donations.max-3-drivers')
              : driversValue.length === 0
                ? t('donations.select-a-driver')
                : t('donations.add-another-driver')
          }
        >
          <MenuItem value="" disabled>
            {t('donations.select-a-driver')}
          </MenuItem>
          {drivers
            .filter((d) => !driversValue.some((dv) => dv.user_id === d.id))
            .map((driver) => (
              <MenuItem key={driver.id} value={driver.id}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar
                    sx={{ width: 32, height: 32 }}
                    src={driver.image ? getS3Url(driver.image) : null}
                  >
                    {driver.name?.slice(0, 2)?.toUpperCase()}
                  </Avatar>
                  <DriverItem driver={driver} />
                </Stack>
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* Added drivers */}
      <Collapse in={!!driversValue.length}>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {driversValue.map((driver) => {
            const driverData = drivers.find((d) => d.id === driver.user_id);

            return (
              <Stack
                key={driver.user_id}
                direction="column"
                spacing={1.5}
                sx={{
                  p: 1.5,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  borderRadius: 1.5,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar
                    sx={{ width: 40, height: 40, flexShrink: 0 }}
                    src={driverData?.image ? getS3Url(driverData.image) : null}
                  >
                    {driverData?.name?.slice(0, 2)?.toUpperCase()}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {driverData?.name || t('common.na')}
                    </Typography>
                    {driverData?.email && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        display="block"
                      >
                        {driverData.email}
                      </Typography>
                    )}
                  </Box>

                  <VehicleSelectField
                    value={driver.vehicle_id || ''}
                    onChange={(vehicleId) =>
                      handleUpdateVehicle(driver.user_id, vehicleId)
                    }
                    vehicles={vehicles}
                    compact
                  />

                  <IconButton
                    edge="end"
                    aria-label={t('common.remove')}
                    onClick={() => handleRemoveDriver(driver.user_id)}
                  >
                    <DeleteOutlined />
                  </IconButton>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Collapse>

      {error ? (
        <Typography variant="body2" color="error" mt={1}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

export default DriverSelector;
